// Shared pin-sheet geometry + rendering logic in millimeters.
// Grid density changes with the outer diameter so pins fit on A4.
import { jsPDF } from "jspdf";

export const PAGE = { W: 210, H: 297, COLS: 3, ROWS: 5, CELLS: 15 };
export const CELLS_PER_PAGE = 15;
export const DEFAULTS = { innerD: 45, outerD: 55.5, gapH: 0, gapV: 3 };

export const GRID_PRESETS = {
  "25": { innerD: 26, outerD: 36.5, COLS: 5, ROWS: 7 },
  "32": { innerD: 33, outerD: 43.5, COLS: 4, ROWS: 6 },
  "44": { innerD: 45, outerD: 55.5, COLS: 3, ROWS: 5 },
  "58": { innerD: 59, outerD: 71.5, COLS: 2, ROWS: 4 },
  "75": { innerD: 76, outerD: 87.5, COLS: 2, ROWS: 3 },
};

// font mappings (editor CSS <-> jsPDF built-in fonts) so preview ~ PDF
export const CSS_FONT = {
  sans: "'Inter', system-ui, sans-serif",
  serif: "Georgia, 'Times New Roman', serif",
  mono: "'JetBrains Mono', monospace",
};
const PDF_FONT = { sans: "helvetica", serif: "times", mono: "courier" };

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

function hexToRgb(hex) {
  const h = (hex || "#111111").replace("#", "");
  const n = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const int = parseInt(n, 16);
  return [(int >> 16) & 255, (int >> 8) & 255, int & 255];
}

// Grid preset mengunci jumlah pin; diameter lingkaran bisa diedit terpisah.
function gridForOuter(outerD) {
  if (outerD <= 28.5) return { COLS: 5, ROWS: 7 };
  if (outerD <= 38.5) return { COLS: 4, ROWS: 6 };
  if (outerD <= 51.5) return { COLS: 3, ROWS: 5 };
  if (outerD <= 66.5) return { COLS: 2, ROWS: 4 };
  return { COLS: 2, ROWS: 3 };
}

export function resolveLayout(innerD = DEFAULTS.innerD, outerD = DEFAULTS.outerD, gridPreset = "44", gapH = DEFAULTS.gapH, gapV = DEFAULTS.gapV) {
  const preset = GRID_PRESETS[String(gridPreset)];
  const requestedOuter = clamp(Number(outerD) || DEFAULTS.outerD, 10, 100);
  const { COLS, ROWS } = preset || gridForOuter(requestedOuter);
  const margin = 5;

  // Diameter maksimum agar seluruh lingkaran tetap di dalam area A4.
  const maxOuterW = (PAGE.W - 2 * margin) / COLS;
  const maxOuterH = (PAGE.H - 2 * margin) / ROWS;
  const maxOuter = Math.max(10, Math.min(maxOuterW, maxOuterH));
  const O = Math.min(requestedOuter, maxOuter);
  const I = clamp(Number(innerD) || DEFAULTS.innerD, 4, O - 0.5);

  const requestedGapH = Number.isFinite(Number(gapH)) ? Math.max(0, Number(gapH)) : DEFAULTS.gapH;
  const requestedGapV = Number.isFinite(Number(gapV)) ? Math.max(0, Number(gapV)) : DEFAULTS.gapV;

  // Batasi jarak agar margin kiri-kanan dan atas-bawah tetap minimal 5 mm.
  const maxGapH = COLS > 1 ? Math.max(0, (PAGE.W - 2 * margin - COLS * O) / (COLS - 1)) : 0;
  const maxGapV = ROWS > 1 ? Math.max(0, (PAGE.H - 2 * margin - ROWS * O) / (ROWS - 1)) : 0;
  const effectiveGapH = Math.min(requestedGapH, maxGapH);
  const effectiveGapV = Math.min(requestedGapV, maxGapV);

  const contentW = COLS * O + (COLS - 1) * effectiveGapH;
  const contentH = ROWS * O + (ROWS - 1) * effectiveGapV;

  return {
    PAGE_W: PAGE.W, PAGE_H: PAGE.H, COLS, ROWS,
    CELLS_PER_PAGE: COLS * ROWS,
    GRID_PRESET: preset ? String(gridPreset) : null,
    OUTER_D: O, INNER_D: I, GAP_H: effectiveGapH, GAP_V: effectiveGapV,
    MARGIN_X: (PAGE.W - contentW) / 2,
    MARGIN_Y: (PAGE.H - contentH) / 2,
  };
}

export const LAYOUT = resolveLayout(); // default (45/54) for convenience

// Nomor berjalan dari atas ke bawah, lalu pindah ke kolom berikutnya.
export function cellCenterMM(index, L = LAYOUT) {
  const col = Math.floor(index / L.ROWS);
  const row = index % L.ROWS;
  const x = L.MARGIN_X + L.OUTER_D / 2 + col * (L.OUTER_D + L.GAP_H);
  const y = L.MARGIN_Y + L.OUTER_D / 2 + row * (L.OUTER_D + L.GAP_V);
  return { x, y, col, row };
}

// Draw a circular-masked image with cover-fit + zoom + pan onto a canvas ctx.
export function drawCircularImage(ctx, img, sizePx, zoom, offX, offY) {
  ctx.save();
  ctx.clearRect(0, 0, sizePx, sizePx);
  ctx.beginPath();
  ctx.arc(sizePx / 2, sizePx / 2, sizePx / 2, 0, Math.PI * 2);
  ctx.closePath();
  ctx.clip();
  const iw = img.naturalWidth || img.width;
  const ih = img.naturalHeight || img.height;
  const base = sizePx / Math.min(iw, ih);
  const scale = base * (zoom || 1);
  const drawW = iw * scale;
  const drawH = ih * scale;
  const dx = (sizePx - drawW) / 2 + (offX || 0) * sizePx;
  const dy = (sizePx - drawH) / 2 + (offY || 0) * sizePx;
  ctx.drawImage(img, dx, dy, drawW, drawH);
  ctx.restore();
}

export function renderCircularDataURL(
  img,
  sizePx,
  zoom,
  offX,
  offY,
  format = "png",
  bgColor = "#ffffff",
  reusableCanvas = null
) {
  // Canvas dapat digunakan ulang untuk setiap gambar dalam satu dokumen PDF.
  const canvas = reusableCanvas || document.createElement("canvas");

  if (canvas.width !== sizePx) canvas.width = sizePx;
  if (canvas.height !== sizePx) canvas.height = sizePx;

  const ctx = canvas.getContext("2d");
  drawCircularImage(ctx, img, sizePx, zoom, offX, offY);

  if (format === "jpeg") {
    ctx.save();
    ctx.globalCompositeOperation = "destination-over";

    ctx.fillStyle = bgColor || "#ffffff";
    ctx.beginPath();
    ctx.arc(sizePx / 2, sizePx / 2, sizePx / 2, 0, Math.PI * 2);
    ctx.closePath();
    ctx.fill();

    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, sizePx, sizePx);
    ctx.restore();
  }

  return format === "jpeg"
    ? canvas.toDataURL("image/jpeg", 0.92)
    : canvas.toDataURL("image/png");
}
// Encode JPEG asynchronously for PDF/print without changing render quality.
export async function renderCircularJPEGBytes(
  img,
  sizePx,
  zoom,
  offX,
  offY,
  bgColor = "#ffffff",
  reusableCanvas = null
) {
  const canvas = reusableCanvas || document.createElement("canvas");

  if (canvas.width !== sizePx) canvas.width = sizePx;
  if (canvas.height !== sizePx) canvas.height = sizePx;

  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas context tidak tersedia.");

  drawCircularImage(ctx, img, sizePx, zoom, offX, offY);

  ctx.save();
  ctx.globalCompositeOperation = "destination-over";

  ctx.fillStyle = bgColor || "#ffffff";
  ctx.beginPath();
  ctx.arc(sizePx / 2, sizePx / 2, sizePx / 2, 0, Math.PI * 2);
  ctx.closePath();
  ctx.fill();

  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, sizePx, sizePx);
  ctx.restore();

  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob((result) => {
      if (!result) {
        reject(new Error("Gagal mengubah gambar menjadi JPEG."));
        return;
      }
      resolve(result);
    }, "image/jpeg", 0.92);
  });

  return new Uint8Array(await blob.arrayBuffer());
}

export function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

// Auto-fit single-line text font size (px) for a given css font family + weight.
export function fitFontPx(text, maxWidthPx, { fontFamily = "sans", bold = false } = {}) {
  if (!text) return 12;
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  const fam = CSS_FONT[fontFamily] || CSS_FONT.sans;
  const weight = bold ? "700" : "400";
  let px = Math.round(maxWidthPx * 0.6);
  for (; px > 6; px--) {
    ctx.font = `${weight} ${px}px ${fam}`;
    if (ctx.measureText(text).width <= maxWidthPx) break;
  }
  return px;
}

// Draw a perfectly continuous stroked circle as ONE closed path (many small
// segments). jsPDF's built-in doc.circle() approximates with 4 bezier arcs whose
// joins can show tiny gaps at high zoom -> this avoids that entirely.
function strokeSmoothCircle(doc, cx, cy, r, segments = 144) {
  const start = [cx + r, cy];
  const deltas = [];
  let prevX = start[0], prevY = start[1];
  for (let i = 1; i <= segments; i++) {
    const a = (i / segments) * Math.PI * 2;
    const x = cx + r * Math.cos(a);
    const y = cy + r * Math.sin(a);
    deltas.push([x - prevX, y - prevY]);
    prevX = x; prevY = y;
  }
  doc.lines(deltas, start[0], start[1], [1, 1], "S", true);
}

// Build the jsPDF document from pages using the given circle settings.
export async function buildPDF(pages, options = {}) {
  const {
    innerD,
    outerD,
    gridPreset = "44",
    gapH = DEFAULTS.gapH,
    gapV = DEFAULTS.gapV,
    showInnerGuide = true,
    showCenter = false,
    centerDotSize = 1.5,
    centerDotColor = "#e22020",
  } = options;

  const L = resolveLayout(innerD, outerD, gridPreset, gapH, gapV);
  const doc = new jsPDF({
    orientation: "p",
    unit: "mm",
    format: "a4",
    floatPrecision: 16,
  });

  // Pertahankan resolusi gambar 300 DPI.
  const ppmm = 300 / 25.4;
  const sizePx = Math.round(L.INNER_D * ppmm);

  // Satu canvas dipakai ulang untuk seluruh gambar dalam dokumen ini.
  const reusableCanvas = document.createElement("canvas");
  reusableCanvas.width = sizePx;
  reusableCanvas.height = sizePx;

  // Batasi jumlah gambar yang didekode bersamaan untuk mengontrol RAM.
  const IMAGE_DECODE_BATCH_SIZE = 4;

  for (let p = 0; p < pages.length; p++) {
    if (p > 0) doc.addPage("a4", "p");

    if (doc.setLineDashPattern) doc.setLineDashPattern([], 0);
    doc.setLineCap("round");
    doc.setLineJoin("round");

    const cells = pages[p].cells;

    for (
      let batchStart = 0;
      batchStart < L.CELLS_PER_PAGE;
      batchStart += IMAGE_DECODE_BATCH_SIZE
    ) {
      const batchEnd = Math.min(
        batchStart + IMAGE_DECODE_BATCH_SIZE,
        L.CELLS_PER_PAGE
      );

      // Mulai decode beberapa gambar secara paralel.
      const imagePromises = new Map();

      for (let i = batchStart; i < batchEnd; i++) {
        const cell = cells[i];
        if (
          cell?.type === "image" &&
          cell.imageData &&
          !imagePromises.has(cell.imageData)
        ) {
          imagePromises.set(cell.imageData, loadImage(cell.imageData));
        }
      }

      const decodedImages = new Map();

      await Promise.all(
        Array.from(imagePromises.entries()).map(async ([source, promise]) => {
          try {
            decodedImages.set(source, await promise);
          } catch (error) {
            console.error("Skipped an image in PDF export:", error);
          }
        })
      );

      // Render tetap berurutan agar posisi, alias, dan hasil PDF tidak berubah.
      for (let i = batchStart; i < batchEnd; i++) {
        const { x, y } = cellCenterMM(i, L);
        const cell = cells[i];

        if (cell && cell.type === "color" && cell.fillColor) {
          const [cr, cg, cb] = hexToRgb(cell.fillColor);
          doc.setFillColor(cr, cg, cb);
          doc.circle(x, y, L.INNER_D / 2, "F");

        } else if (cell && cell.type === "image" && cell.imageData) {
          const img = decodedImages.get(cell.imageData);

          if (img) {
            try {
              const imageBytes = await renderCircularJPEGBytes(
                img,
                sizePx,
                cell.zoom,
                cell.offX,
                cell.offY,
                cell.bgColor || "#ffffff",
                reusableCanvas
              );

              doc.addImage(
                imageBytes,
                "JPEG",
                x - L.INNER_D / 2,
                y - L.INNER_D / 2,
                L.INNER_D,
                L.INNER_D,
                `pin_${p}_${i}`
              );
            } catch (error) {
              console.error("Skipped an image in PDF export:", error);
            }
          }

        } else if (cell && cell.type === "text" && cell.text) {
          const fam = PDF_FONT[cell.fontFamily || "sans"] || "helvetica";
          doc.setFont(fam, cell.bold ? "bold" : "normal");

          let pt = 40;
          const maxWidth = L.INNER_D * 0.82;
          doc.setFontSize(pt);

          while (doc.getTextWidth(cell.text) > maxWidth && pt > 4) {
            pt -= 1;
            doc.setFontSize(pt);
          }

          const [r, g, b] = hexToRgb(cell.color);
          doc.setTextColor(r, g, b);
          doc.text(cell.text, x, y, {
            align: "center",
            baseline: "middle",
          });
        }

        // Garis panduan tetap digambar di atas isi sel.
        doc.setDrawColor(90);
        doc.setLineWidth(0.2);
        strokeSmoothCircle(doc, x, y, L.OUTER_D / 2);

        if (showInnerGuide) {
          doc.setDrawColor(200);
          doc.setLineWidth(0.1);
          strokeSmoothCircle(doc, x, y, L.INNER_D / 2);
        }

        if (showCenter) {
          const midR = (L.INNER_D / 2 + L.OUTER_D / 2) / 2;
          const [dr, dg, dbb] = hexToRgb(centerDotColor);
          doc.setFillColor(dr, dg, dbb);
          doc.circle(
            x,
            y - midR,
            Math.max(0.2, (centerDotSize || 2.4) / 2),
            "F"
          );
        }
      }
    }
  }

  return doc;
}
export async function exportPDF(pages, filename = "pin-sheet.pdf", options = {}) {
  const doc = await buildPDF(pages, options);
  doc.save(filename);
}

export async function printPDF(pages, options = {}) {
  // Buat PDF sampai selesai sebelum membuka tab baru.
  const doc = await buildPDF(pages, options);
  const blob = doc.output("blob");

  if (!blob || blob.size === 0) {
    throw new Error("PDF hasil cetak kosong.");
  }

  const blobUrl = URL.createObjectURL(blob);
  let printTab = null;

  try {
    // Tab baru hanya dibuka setelah seluruh PDF selesai dibuat.
    printTab = window.open(blobUrl, "_blank");

    if (!printTab) {
      throw new Error(
        "Chrome memblokir tab cetak. Izinkan pop-up untuk PIN Maker Studio, lalu coba lagi."
      );
    }

    let printStarted = false;

    const startPrint = () => {
      if (printStarted || printTab.closed) return;
      printStarted = true;

      try {
        printTab.focus();
        printTab.print();
      } catch (error) {
        console.warn("[PDF Print] Print preview belum dapat dibuka:", error);
      }
    };

    // Tunggu PDF viewer selesai dimuat sebelum memanggil print.
    printTab.addEventListener(
      "load",
      () => window.setTimeout(startPrint, 1200),
      { once: true }
    );

    // Cadangan jika Chrome tidak mengirim event load dari PDF viewer.
    window.setTimeout(startPrint, 5000);

    // Pertahankan Blob URL selama sesi cetak.
    window.setTimeout(() => {
      URL.revokeObjectURL(blobUrl);
    }, 30 * 60 * 1000);

    console.info(
      "[PDF Print] PMS_PRINT_OPEN_AFTER_READY_V1",
      "| bytes:",
      blob.size
    );
  } catch (error) {
    if (printTab && !printTab.closed) printTab.close();
    URL.revokeObjectURL(blobUrl);
    throw error;
  }
}
// ---------- Fill-order helpers ----------
export function findNextEmpty(pages) {
  for (let p = 0; p < pages.length; p++) {
    for (let i = 0; i < pages[p].cells.length; i++) {
      if (pages[p].cells[i]?.type === "empty") return { pageIdx: p, cellIdx: i };
    }
  }
  return null;
}

export function emptyCell(i) {
  return {
    index: i, type: "empty", imageId: null, imageData: null, text: null, fillColor: null, bgColor: null,
    zoom: 1, offX: 0, offY: 0, fontFamily: "sans", bold: true, color: "#111111",
  };
}

export function makeEmptyPage(count = CELLS_PER_PAGE) {
  return { cells: Array.from({ length: count }, (_, i) => emptyCell(i)) };
}
